import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { tones, type ToneName } from '@/theme';

/**
 * Píldora de estado / etiqueta.
 *
 * La referencia visual usa dos formas para lo mismo:
 *  - texto coloreado sin fondo (deltas tipo "+2.24%") → `text` de `tones[...]`;
 *  - píldora con fondo suave, texto oscuro y punto indicador → este componente.
 */

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ToneName;
  /** Punto de color a la izquierda (útil para estados de orden/lead). */
  dot?: boolean;
  icon?: ReactNode;
}

export function Badge({ tone = 'neutral', dot = false, icon, className, children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-1 text-micro font-semibold whitespace-nowrap',
        tones[tone].className,
        className,
      )}
      {...props}
    >
      {dot ? <span className={cn('size-1.5 rounded-pill', tones[tone].dot)} aria-hidden="true" /> : null}
      {icon}
      {children}
    </span>
  );
}

/** Badge de un estado del dominio (ver `src/theme/status.ts`). */
export function StatusBadge({
  label,
  tone,
  className,
}: {
  label: string;
  tone: ToneName;
  className?: string;
}) {
  return (
    <Badge tone={tone} dot className={className}>
      {label}
    </Badge>
  );
}
